"use client";

import * as React from "react";
import { confirmDialog, type ConfirmDialogOptions } from "./confirm-dialog";

/**
 * #13715 — the "Discard changes?" ask. DialogContent and SheetContent take a `dirty`
 * prop. When it is true, Escape, an outside click, the close (X) and the Sheet's static
 * backdrop ask first; "Keep editing" keeps the panel open. When it is false or absent,
 * nothing changes.
 */
export const DISCARD_CHANGES: ConfirmDialogOptions = {
  title: "Discard changes?",
  description: "You have unsaved changes. If you close now, they are lost.",
  confirmText: "Discard",
  cancelText: "Keep editing",
  destructive: true,
};

type Preventable = { preventDefault(): void };

/**
 * The dismiss guard for a Radix dialog Content. `ask(event)` cancels the dismiss when
 * `dirty` and opens the confirm; on "Discard" it clicks `closeRef` (a hidden Radix Close),
 * so the Root's own onOpenChange(false) runs. One ask at a time.
 */
export function useDirtyDismiss(dirty: boolean | undefined) {
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const asking = React.useRef(false);
  const ask = React.useCallback(
    (event: Preventable) => {
      if (!dirty) return;
      event.preventDefault();
      if (asking.current) return;
      asking.current = true;
      void confirmDialog(DISCARD_CHANGES).then((discard) => {
        asking.current = false;
        if (discard) closeRef.current?.click();
      });
    },
    [dirty],
  );
  return { closeRef, ask };
}

/** Run the caller's handler, then the guard unless the caller already cancelled. */
export function guarded<E extends Preventable & { defaultPrevented?: boolean }>(
  ask: (event: E) => void,
  handler?: (event: E) => void,
) {
  return (event: E) => {
    handler?.(event);
    if (!event.defaultPrevented) ask(event);
  };
}

/**
 * #13715 Slice B — warn before a reload or tab close while `dirty`. For full-page editors
 * (a Dialog or Sheet uses the `dirty` prop instead). The browser shows its own text.
 */
export function useBeforeUnload(dirty: boolean | undefined) {
  React.useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
}
